import { animate } from 'motion/mini';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { SHORTCUT_HINT } from 'components/global-search/GlobalSearchBar';
import { AutoAwesome } from 'components/icons';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverDescription, PopoverTitle } from 'components/ui/popover';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import type { TourStep } from './steps';
import type { TourAnchor } from './TourSpotlight';

/**
 * The stripe along the top of a tour card: the brand's greens running into its amber. It blends in OKLCH,
 * through the yellow-greens between them; sRGB would turn the middle khaki.
 */
export const TOUR_STRIPE_CLASS =
    'h-1.5 bg-[linear-gradient(120deg_in_oklch,var(--primary-strong),var(--primary)_45%,var(--secondary))]';

/** The ring's move, `duration-300`: the card glides, or cross-fades, in the same time. */
const MOVE_MS = 300;

/** The card's room on the screen: 8px from its edges, and from the tour's banner at the top. */
const EDGE = 8;

/**
 * The height of the tour's banner, which grows as its sentence wraps on a narrow window. The banner keeps it on
 * the document as `--tour-banner-h`, after this card first renders: until then it is the banner's one line.
 */
export function useTourBannerHeight(): number {
    const [height, setHeight] = useState(36);
    useLayoutEffect(() => {
        const root = document.documentElement;
        const read = () => setHeight(parseFloat(root.style.getPropertyValue('--tour-banner-h')) || 36);
        read();
        const styled = new MutationObserver(read);
        styled.observe(root, { attributeFilter: ['style'] });
        return () => styled.disconnect();
    }, []);
    return height;
}

type Props = {
    step: TourStep;
    /** Where the running tour's copy is: `<wording>.<step id>.title` and `.body`. */
    wording: string;
    /** Zero-based, among the steps left for this user. */
    index: number;
    total: number;
    anchor: TourAnchor;
    onNext: () => void;
    onBack: () => void;
    onSkip: () => void;
};

/**
 * One step of the tour, beside the element it explains or under it, as the step says, in the Welcome popover's
 * style. It flips to another side when there is no room, and where no side has any, as beside a target as wide as a
 * phone's screen, it stays on the screen over the target; the keys are the provider's, so the card takes the focus
 * itself rather than drawing a ring on Next. It stays open from step to step and moves with the spotlight,
 * cross-fading when it changes sides, and its text is announced as it changes. Under reduced motion it jumps to
 * each step instead, dipping out and back in so the jump is not the only sign that it moved.
 */
export default function TourCard({ step, wording, index, total, anchor, onNext, onBack, onSkip }: Props) {
    const cardRef = useRef<HTMLDivElement>(null);
    const isLast = index === total - 1;
    const { id: stepId, demo: Demo, side = 'right' } = step;
    // The first placement is a jump: until it is placed the card sits at the page's top-left, invisible, and a
    // transition would slide it in from there. Every later step glides it, on the same side of its target.
    const [firstStepId] = useState(stepId);
    const [moves, setMoves] = useState(false);
    if (!moves && stepId !== firstStepId) {
        setMoves(true);
    }
    const still = useReducedMotion();
    const bannerHeight = useTourBannerHeight();
    const room = useMemo(() => ({ top: bannerHeight + EDGE, right: EDGE, bottom: EDGE, left: EDGE }), [bannerHeight]);

    // Moving to another side of the target, the card would slide across the ring: it fades out where it was and back
    // in at its new place instead. Base UI writes the new side and position in one commit; the card, measured before
    // the glide is cut short, still shows where it was. The card moves, not the positioner: Motion leaves its last
    // frame inline, and the positioner's transform is Base UI's.
    useLayoutEffect(() => {
        const card = cardRef.current;
        const positioner = card?.parentElement;
        if (!moves || still || !card || !positioner) return undefined;
        const observer = new MutationObserver(() => {
            const from = card.getBoundingClientRect();
            [positioner, card].forEach((node) => node.getAnimations().forEach((animation) => animation.finish()));
            const to = card.getBoundingClientRect();
            const was = `translate(${from.left - to.left}px, ${from.top - to.top}px)`;
            animate(
                card,
                { transform: [was, was, 'none', 'none'], opacity: [1, 0, 0, 1] },
                { duration: MOVE_MS / 1000, ease: 'linear', times: [0, 0.5, 0.5, 1] }
            );
        });
        observer.observe(positioner, { attributeFilter: ['data-side'] });
        return () => observer.disconnect();
    }, [moves, still]);

    useEffect(() => {
        if (moves && still && cardRef.current) {
            animate(cardRef.current, { opacity: [1, 0, 1] }, { duration: 0.15, ease: 'linear' });
        }
    }, [stepId, moves, still]);

    return (
        <Popover open>
            <PopoverContent
                anchor={anchor}
                side={side}
                align="start"
                sideOffset={16}
                collisionPadding={room}
                sticky
                positionerClassName={cn(
                    'z-(--z-tour)',
                    moves && 'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out'
                )}
                className="w-90 max-w-[calc(100vw-16px)] gap-0 overflow-hidden rounded-[12px] p-0"
                ref={cardRef}
                initialFocus={cardRef}
                finalFocus={false}
            >
                <div aria-hidden className={TOUR_STRIPE_CLASS} />
                <div aria-live="polite" className="flex flex-col gap-2.5 px-5 py-4">
                    <div className="flex items-center gap-1.5">
                        {Array.from({ length: total }, (_, dot) => (
                            // oxlint-disable-next-line react-doctor/no-array-index-as-key -- one dot per position, never reordered
                            <span
                                key={dot}
                                aria-hidden
                                className={cn(
                                    'h-1.5 rounded-full motion-safe:transition-[width,background-color] motion-safe:duration-300 motion-safe:ease-out',
                                    dot === index ? 'bg-primary w-4' : 'w-1.5',
                                    dot < index ? 'bg-primary/40' : dot > index && 'bg-primary/15'
                                )}
                            />
                        ))}
                        <span className="text-caption text-muted-foreground ms-auto font-semibold tabular-nums">
                            {t('tour.progress', { n: String(index + 1), total: String(total) })}
                        </span>
                    </div>
                    <PopoverTitle className="text-dialog-title flex items-center gap-2.5 font-semibold">
                        <AutoAwesome className="text-primary size-5" />
                        {t(`${wording}.${stepId}.title`)}
                    </PopoverTitle>
                    <PopoverDescription className="text-foreground-secondary leading-[1.45] text-pretty">
                        {t(`${wording}.${stepId}.body`, { shortcut: SHORTCUT_HINT })}
                    </PopoverDescription>
                    {Demo && <Demo />}
                    <div className="flex items-center gap-2.5 pt-1">
                        {!isLast && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-foreground-tertiary text-meta h-auto px-0 underline"
                                onClick={onSkip}
                            >
                                {t('tour.exit')}
                            </Button>
                        )}
                        <span className="flex-1" />
                        {index > 0 && (
                            <Button variant="outline" className="h-[34px] px-3.5" onClick={onBack}>
                                {t('back')}
                            </Button>
                        )}
                        <Button variant="secondary" className="h-[34px] px-3.5 font-semibold" onClick={onNext}>
                            {t(isLast ? 'tour.finish' : 'next')}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
